import { Avatar } from './Avatar';
import { FoundingBadge } from './FoundingBadge';
import { FlagIcon } from './FlagIcon';
import { Text } from './ui';
import { resolveSocialEntries } from '../lib/socials';
import { countryName } from '../lib/countries';
import type { Profile } from '../lib/types';

/**
 * The public-facing identity card for a member - avatar, name, @handle, badges,
 * location, social icons, bio. Shared by the public profile page (/u/:handle)
 * and the owner's "Preview" tab so the two never drift: the preview shows
 * EXACTLY what other members see.
 */
export function ProfileCard({ profile }: { profile: Profile }) {
  const founding = profile.is_founding_member ?? false;
  const socials = resolveSocialEntries(profile.social_links, profile.instagram_handle);
  const locationText = [profile.location, countryName(profile.country)].filter(Boolean).join(', ');

  return (
    <section className="profile__identity">
      <div className="profile__identity-head">
        <Avatar
          name={profile.display_name || 'Member'}
          src={profile.avatar_url}
          size="lg"
          founding={founding}
        />
        <div className="profile__identity-text">
          <h1 className="profile__name">{profile.display_name || 'Member'}</h1>
          {profile.handle && (
            <p className="profile__handle">
              @{profile.handle}
              {founding && <FoundingBadge size={15} className="profile__handle-seal" />}
            </p>
          )}
          {(founding || profile.role !== 'user') && (
            <div className="profile__tags">
              {founding && <span className="profile__tag">Founding Member</span>}
              {profile.role !== 'user' && (
                <span className="profile__tag profile__tag--role">{profile.role}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {locationText && (
        <p className="profile__location-line">
          <FlagIcon code={profile.country} /> {locationText}
        </p>
      )}

      {socials.length > 0 && (
        <div className="profile__section">
          <ul className="profile__socials" aria-label="Social profiles">
            {socials.map(({ platform, value }) => (
              <li key={platform.id}>
                <a
                  className="profile__social"
                  href={platform.urlFor(value)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={platform.label}
                  title={platform.label}
                >
                  <span className="profile__social-box"><platform.Icon /></span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {profile.bio && <Text role="body" className="profile__bio">{profile.bio}</Text>}

      {profile.fabric_selection && (
        <div className="profile__treasured">
          <Text role="eyebrow" faint className="profile__treasured-label">Most treasured garment</Text>
          <Text role="body" className="profile__treasured-text">{profile.fabric_selection}</Text>
        </div>
      )}
    </section>
  );
}
